import React, { useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import { resolveImageUrl } from '../utils/imageUrl';
import ListPagination, { useListPagination } from './ui/ListPagination';
import { tableHeaderSx } from '../styles/tokens';

const statusStyles = {
  Active: {
    bg: 'rgba(34, 197, 94, 0.12)',
    dot: '#16a34a',
    text: '#15803d',
  },
  Inactive: {
    bg: 'rgba(107, 114, 128, 0.15)',
    dot: '#6b7280',
    text: '#4b5563',
  },
};

const ProductList = ({ products, onEdit, onToggleActive, viewOnly = false, listReturnPath = '/products' }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const openProductDetails = (id) => {
    navigate(`/products/${id}`, { state: { fromProducts: listReturnPath } });
  };

  const sortedProducts = useMemo(() => [...products].sort((a, b) => {
    const categoryA = (a.category || '').toLowerCase();
    const categoryB = (b.category || '').toLowerCase();
    if (categoryA !== categoryB) return categoryA.localeCompare(categoryB);
    return (a.name || '').localeCompare(b.name || '');
  }), [products]);

  const {
    paginatedItems: paginatedProducts,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    totalCount,
  } = useListPagination(sortedProducts, [products.length]);

  const getInitials = (product) => {
    if (product.name && product.name.length >= 2) {
      return product.name.slice(0, 2).toUpperCase();
    }
    if (product.product_code && product.product_code.length >= 2) {
      return product.product_code.slice(0, 2).toUpperCase();
    }
    return (product.name || product.product_code || '??').slice(0, 2).toUpperCase();
  };

  const handleImageClick = (id) => {
    openProductDetails(id);
  };

  return (
    <>
    <Paper
      elevation={0}
      sx={{
        overflow: 'hidden',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        boxShadow: '0 1px 3px 0 rgba(0,0,0,0.08)',
        width: '100%',
        minWidth: 0,
      }}
    >
      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 2 }}>
          {sortedProducts.length === 0 ? (
            <Typography align="center" sx={{ py: 4, color: 'text.secondary', fontSize: '0.9375rem' }}>
              No products available.
            </Typography>
          ) : (
            paginatedProducts.map((product) => {
              const status = product.is_active !== false ? 'Active' : 'Inactive';
              const sc = statusStyles[status];
              return (
                <Paper
                  key={product.id}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box
                      onClick={() => handleImageClick(product.id)}
                      sx={{
                        width: 48,
                        height: 48,
                        flexShrink: 0,
                        borderRadius: 1.25,
                        bgcolor: 'action.hover',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        color: 'text.secondary',
                        cursor: 'pointer',
                        overflow: 'hidden',
                        '&:hover': { bgcolor: 'grey.300' },
                      }}
                    >
                      {product.image_url ? (
                        <Box
                          component="img"
                          src={resolveImageUrl(product.image_url)}
                          alt=""
                          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        ((product.product_code || '').slice(0, 2) || getInitials(product)).toUpperCase()
                      )}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary' }}>
                        {product.name}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5, alignItems: 'center' }}>
                        <Box
                          component="span"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.8125rem',
                            color: 'text.secondary',
                            bgcolor: 'action.hover',
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                          }}
                        >
                          {product.product_code || '—'}
                        </Box>
                        <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                          {product.category || '—'}
                        </Typography>
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.875rem' }}>
                          <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: sc.dot }} />
                          {status}
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, mt: 0.75, fontSize: '0.875rem', color: 'text.secondary' }}>
                        {!viewOnly && <span>Rent ₹{product.price}</span>}
                        <span>Actual: {product.actual_quantity}</span>
                        <span>Current: {product.current_quantity}</span>
                      </Box>
                      {product.description && (
                        <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary', mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {product.description}
                        </Typography>
                      )}
                      {!viewOnly && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); openProductDetails(product.id); }} sx={{ width: 28, height: 28 }} title="View">
                            <VisibilityIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(product); }} sx={{ width: 28, height: 28 }} title="Edit">
                            <EditIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                          {onToggleActive && (
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); onToggleActive(product); }}
                              sx={{ width: 28, height: 28 }}
                              title={product.is_active !== false ? 'Mark inactive' : 'Mark active'}
                            >
                              {product.is_active !== false ? <DeleteIcon sx={{ fontSize: 16 }} /> : <CheckCircleIcon sx={{ fontSize: 16 }} />}
                            </IconButton>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Paper>
              );
            })
          )}
        </Box>
      ) : (
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ ...tableHeaderSx, width: 64 }}>Image</TableCell>
                <TableCell sx={tableHeaderSx}>Name</TableCell>
                <TableCell sx={tableHeaderSx}>Code</TableCell>
                <TableCell sx={tableHeaderSx}>Category</TableCell>
                <TableCell sx={tableHeaderSx}>Status</TableCell>
                {!viewOnly && (
                  <TableCell align="right" sx={tableHeaderSx}>Rent</TableCell>
                )}
                <TableCell align="right" sx={tableHeaderSx}>Actual Stock</TableCell>
                <TableCell align="right" sx={tableHeaderSx}>Current Stock</TableCell>
                <TableCell sx={tableHeaderSx}>Description</TableCell>
                {!viewOnly && (
                  <TableCell align="center" sx={{ ...tableHeaderSx, width: 112 }}>Actions</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedProducts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={viewOnly ? 8 : 10} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    No products available.
                  </TableCell>
                </TableRow>
              ) : (
                paginatedProducts.map((product) => {
                const status = product.is_active !== false ? 'Active' : 'Inactive';
                const sc = statusStyles[status];
                return (
                  <TableRow
                    key={product.id}
                    sx={{
                      '&:hover': { bgcolor: 'action.hover' },
                      '&:hover .actions-buttons': { opacity: 1 },
                      transition: 'background-color 0.15s',
                    }}
                  >
                    <TableCell sx={{ px: 2, py: 1.5 }}>
                      <Box
                        onClick={() => handleImageClick(product.id)}
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1.25,
                          bgcolor: 'action.hover',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          color: 'text.secondary',
                          cursor: 'pointer',
                          overflow: 'hidden',
                          '&:hover': { bgcolor: 'grey.300' },
                        }}
                      >
                        {product.image_url ? (
                          <Box
                            component="img"
                          src={resolveImageUrl(product.image_url)}
                            alt=""
                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          ((product.product_code || '').slice(0, 2) || getInitials(product)).toUpperCase()
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ px: 2, py: 1.5 }}>
                      <Typography component="span" sx={{ fontSize: '0.9375rem', color: 'text.primary' }}>
                        {product.name}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ px: 2, py: 1.5 }}>
                      <Box
                        component="span"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.8125rem',
                          color: 'text.secondary',
                          bgcolor: 'action.hover',
                          px: 1.25,
                          py: 0.5,
                          borderRadius: 1,
                        }}
                      >
                        {product.product_code || '—'}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ px: 2, py: 1.5, fontSize: '0.9375rem', color: 'text.secondary' }}>
                      {product.category || '—'}
                    </TableCell>
                    <TableCell sx={{ px: 2, py: 1.5 }}>
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.75,
                          fontSize: '0.875rem',
                          color: 'text.primary',
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: sc.dot,
                          }}
                        />
                        {status}
                      </Box>
                    </TableCell>
                    {!viewOnly && (
                      <TableCell align="right" sx={{ px: 2, py: 1.5 }}>
                        <Typography component="span" sx={{ fontSize: '0.9375rem', color: 'text.primary' }}>
                          ₹{product.price}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell align="right" sx={{ px: 2, py: 1.5, fontSize: '0.9375rem', color: 'text.secondary' }}>
                      {product.actual_quantity}
                    </TableCell>
                    <TableCell align="right" sx={{ px: 2, py: 1.5 }}>
                      <Box component="span" sx={{ fontSize: '0.9375rem', color: 'text.primary' }}>
                        {product.current_quantity}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ px: 2, py: 1.5, fontSize: '0.9375rem', color: 'text.secondary' }}>
                      {product.description || '—'}
                    </TableCell>
                    {!viewOnly && (
                      <TableCell align="center" sx={{ px: 2, py: 1.5 }}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              openProductDetails(product.id);
                            }}
                            sx={{
                              width: 28,
                              height: 28,
                              color: 'text.secondary',
                              borderRadius: 1.25,
                              '&:hover': { bgcolor: 'action.hover', color: 'primary.main' },
                            }}
                            title="View details"
                          >
                            <VisibilityIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEdit(product);
                            }}
                            sx={{
                              width: 28,
                              height: 28,
                              color: 'text.secondary',
                              borderRadius: 1.25,
                              '&:hover': { bgcolor: 'action.hover', color: 'primary.main' },
                            }}
                            title="Edit"
                          >
                            <EditIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                          {onToggleActive && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onToggleActive(product);
                              }}
                              sx={{
                                width: 28,
                                height: 28,
                                color: product.is_active !== false ? 'text.secondary' : 'success.main',
                                borderRadius: 1.25,
                                '&:hover': {
                                  bgcolor: 'action.hover',
                                  color: product.is_active !== false ? 'error.main' : 'success.dark',
                                },
                              }}
                              title={product.is_active !== false ? 'Mark inactive' : 'Mark active'}
                            >
                              {product.is_active !== false ? (
                                <DeleteIcon sx={{ fontSize: 16 }} />
                              ) : (
                                <CheckCircleIcon sx={{ fontSize: 16 }} />
                              )}
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ListPagination
        page={page}
        totalCount={totalCount}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        itemLabel="product"
      />
    </Paper>
    </>
  );
};

export default ProductList;
